/**
 * Outreach lists — thin typed client over /api/crm/outreach-lists (docs/crm-outreach-lists-plan.md §B10).
 * Every call surfaces the server's plain-sentence `error`; `isUnavailable` lets new surfaces hide
 * themselves while the backend half is not deployed yet (plan §8 push order: 404/5xx = "not yet").
 */
import api from '../../services/api';
import type {
  LeadOutreachInfo,
  OutreachCreateRequest,
  OutreachCreateResponse,
  OutreachListDetail,
  OutreachListSummary,
  OutreachMembersPage,
  OutreachOutcomeRequest,
  OutreachOutcomeResponse,
  OutreachPreflightRequest,
  OutreachPreflightResponse,
} from './types';

const BASE = '/api/crm/outreach-lists';

/** The plain sentence to show for a failed call. Never a raw status line or stack. */
export function outreachErrorMessage(e: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const data = (e as any)?.response?.data;
  if (data && typeof data.error === 'string' && data.error.trim()) return data.error;
  if ((e as any)?.response?.status === 403) return "You don't have access to that.";
  if ((e as any)?.response?.status === 404) return "That outreach list wasn't found.";
  return fallback;
}

/** True when the feature isn't served by this api yet (older deploy) or the api is down. */
export function isUnavailable(e: unknown): boolean {
  const status = (e as any)?.response?.status;
  return status === 404 || status === 502 || status === 503 || !(e as any)?.response;
}

export async function preflightOutreach(body: OutreachPreflightRequest): Promise<OutreachPreflightResponse> {
  const { data } = await api.post(`${BASE}/preflight`, body);
  return data;
}

export async function createOutreachList(body: OutreachCreateRequest): Promise<OutreachCreateResponse> {
  const { data } = await api.post(BASE, body);
  return data;
}

export async function listOutreachLists(scope: 'mine' | 'all' = 'mine'): Promise<OutreachListSummary[]> {
  const { data } = await api.get(BASE, { params: { scope } });
  return data?.lists ?? [];
}

export async function getOutreachList(id: string): Promise<OutreachListDetail> {
  const { data } = await api.get(`${BASE}/${id}`);
  return data?.list;
}

export async function getOutreachMembers(
  id: string,
  params: { assignee?: string; state?: string; outcome?: string; bucket?: string; offset?: number; limit?: number } = {},
): Promise<OutreachMembersPage> {
  const { data } = await api.get(`${BASE}/${id}/members`, { params });
  return { members: data?.members ?? [], total: data?.total ?? 0 };
}

export async function logOutreachOutcome(
  listId: string,
  memberId: string,
  body: OutreachOutcomeRequest,
): Promise<OutreachOutcomeResponse> {
  const { data } = await api.post(`${BASE}/${listId}/members/${memberId}/outcome`, body);
  return data;
}

export async function reassignOutreachMembers(listId: string, memberIds: string[], toUserId: string): Promise<{ moved: number }> {
  const { data } = await api.post(`${BASE}/${listId}/members/reassign`, { member_ids: memberIds, to_user_id: toUserId });
  return data;
}

export async function removeOutreachMembers(listId: string, memberIds: string[]): Promise<{ removed: number }> {
  const { data } = await api.post(`${BASE}/${listId}/members/remove`, { member_ids: memberIds });
  return data;
}

export async function shareOutUntouched(listId: string, userIds: string[]): Promise<{ moved: number }> {
  const { data } = await api.post(`${BASE}/${listId}/share-out`, { user_ids: userIds });
  return data;
}

export async function cancelOutreachTexts(listId: string): Promise<{ cancelled: number }> {
  const { data } = await api.post(`${BASE}/${listId}/cancel-texts`);
  return data;
}

export async function sendOutreachAgentTexts(listId: string): Promise<{ batches: number; scheduled: number }> {
  const { data } = await api.post(`${BASE}/${listId}/send-agent-texts`);
  return data;
}

export async function updateOutreachList(
  listId: string,
  patch: { name?: string; status?: 'completed'; archive_unreached?: boolean },
): Promise<OutreachListDetail> {
  const { data } = await api.patch(`${BASE}/${listId}`, patch);
  return data?.list;
}

export async function getLeadOutreach(contactId: string): Promise<LeadOutreachInfo> {
  const { data } = await api.get(`/api/crm/contacts/${contactId}/outreach-list`);
  return { member: data?.member ?? null };
}
